import { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { StaggeredText } from './StaggeredText';
import { PainterlyWater } from './PainterlyWater';
import { SubtleStars } from './AtmosphericGrain';
import { DoodleStar } from './scrapbook/DoodleStar';
import { ConstellationLine } from './scrapbook/ConstellationLine';
import { OrbitLine } from './scrapbook/OrbitLine';
import './NightSky.css';

export function HeroSection() {
  const { scrollY } = useScroll();

  // Generate star positions once and keep them static
  const accentStars = useMemo(() => {
    return Array.from({ length: 8 }).map((_, i) => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 60}%`,
      size: 3 + Math.random() * 1.5,
      delay: Math.random() * 4,
      opacity: 0.4 + Math.random() * 0.3,
    }));
  }, []);

  // Parallax for moon only - content stays static
  const moonY = useTransform(scrollY, [0, 1000], [0, 100]);
  const skyOpacity = useTransform(scrollY, [0, 600], [1, 0]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Night Sky Background */}
      <div className="absolute inset-0 night-sky" />

      {/* Stars - static, no movement */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: skyOpacity }}
      >
        <SubtleStars count={80} className="opacity-60" />

        {/* Larger accent stars - scattered sparingly */}
        <div className="absolute inset-0 pointer-events-none opacity-50">
          {accentStars.map((star, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-[#ffe2bc]"
              style={{
                left: star.left,
                top: star.top,
                width: `${star.size}px`,
                height: `${star.size}px`,
                opacity: star.opacity,
                animation: `twinkle ${3 + star.delay}s ease-in-out infinite`,
                animationDelay: `${star.delay}s`,
                boxShadow: '0 0 4px rgba(255, 226, 188, 0.3)',
              }}
            />
          ))}
        </div>

        {/* Planet in upper right */}
        <div className="absolute top-[15%] right-[12%]">
          <img
            src="/images/planet.png"
            alt="Planet"
            className="h-[52px] opacity-80"
            style={{
              filter: 'brightness(1.05)',
              transform: 'rotate(45deg)',
            }}
          />
          {/* Tiny orbit sketch around Saturn */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <OrbitLine width={70} height={36} color="rgba(255, 226, 188, 0.25)" />
          </div>
        </div>

        {/* Hand-drawn celestial details - very sparse */}
        {/* Doodle star - upper left area */}
        <div className="absolute top-[20%] left-[25%] pointer-events-none opacity-70">
          <DoodleStar size={16} style={{ transform: 'rotate(-8deg)' }} />
        </div>

        {/* Tiny constellation - left side */}
        <div className="absolute top-[28%] left-[15%] pointer-events-none opacity-50">
          <ConstellationLine />
        </div>
      </motion.div>

      {/* Moon - with parallax */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ y: moonY, opacity: skyOpacity }}
      >
        <div className="moon">
          <div className="moon-glow" />
          <img
            src="/images/moon.png"
            alt="Moon"
            className="moon-image"
          />
        </div>
      </motion.div>

      {/* Ocean Surface - starts near bottom of hero, bleeds into next section */}
      <div className="absolute bottom-[-20vh] left-0 right-0 h-[60vh] pointer-events-none">
        {/* Distinct water background - darker and more visible */}
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(34,65,93,0.6)] via-[rgba(29,56,80,0.8)] to-[rgba(23,46,66,0.95)]" />

        {/* Clearer horizon line to separate sky from water */}
        <div className="absolute top-[25%] left-0 right-0">
          {/* Main horizon line */}
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#ffe2bc]/30 to-transparent" />
          {/* Atmospheric glow above horizon */}
          <div className="absolute top-[-40px] left-0 right-0 h-[40px] bg-gradient-to-b from-transparent to-[#ffe2bc]/10" />
        </div>

        {/* Canvas water reflection overlay */}
        <div className="absolute inset-0">
          <PainterlyWater lightIntensity={1} />
          {/* Fade overlay at bottom to blend surface away smoothly */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[rgba(13,26,45,0.5)] pointer-events-none" style={{ top: '50%' }} />
        </div>

        {/* Sticker10 sailing across the water */}
        <motion.img
          src="/images/sticker10.png"
          alt=""
          className="absolute top-[35%] w-24 h-auto pointer-events-none z-[5]"
          style={{
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
            opacity: contentOpacity,
          }}
          animate={{
            x: ['-10vw', '110vw'],
            y: [0, 8, 0, 8, 0, 8, 0]
          }}
          transition={{
            x: { duration: 120, repeat: Infinity, ease: "linear" },
            y: { duration: 3, repeat: Infinity, ease: "easeInOut" }
          }}
        />
      </div>

      {/* Content Overlay - static with fade */}
      <motion.div
        className="max-w-7xl mx-auto px-6 relative z-10 text-center pt-32 lg:pt-40"
        style={{ opacity: contentOpacity }}
      >
        <div className="space-y-5 max-w-4xl mx-auto">
          {/* Large Editorial Headline with Staggered Reveal */}
          <h1 className="text-6xl lg:text-8xl text-[#fff5eb] font-bold leading-[1.1]">
            <StaggeredText delay={0.2} stagger={0.15}>
              UX Researcher & Developer
            </StaggeredText>
          </h1>

          {/* Subtitle */}
          <motion.p
            className="text-xl lg:text-2xl text-[#ffe2bc]/70 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            Exploring how technology can create accessible, meaningful, and educational experiences.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
          >
            <a
              href="#projects"
              className="px-8 py-3 bg-[#ffe2bc] text-slate-900 rounded-lg hover:bg-[#fff5eb] transition-all duration-300 font-medium shadow-lg shadow-[#ffe2bc]/20 hover:shadow-[#ffe2bc]/40 hover:scale-105"
            >
              View Projects
            </a>
            <a
              href="#contact"
              className="px-8 py-3 bg-slate-800/50 text-[#ffe2bc] rounded-lg hover:bg-slate-700/50 border border-[#d4ae75]/30 transition-all duration-300 backdrop-blur-sm hover:scale-105"
            >
              Get in Touch
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.a
        href="#projects"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#ffe2bc]/50 hover:text-[#ffe2bc] transition-colors z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDown className="w-8 h-8" />
      </motion.a>
    </section>
  );
}
